import React, { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  MenuItem,
  Pagination,
  TextField,
  Typography
} from '@mui/material';
import { Assignment } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { complaintsAPI } from '../services/api';

const STATUSES = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'];
const PAGE_SIZE = 20;
const toDateInput = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const statusColor = status => ({
  OPEN: 'error',
  IN_PROGRESS: 'warning',
  RESOLVED: 'success',
  CLOSED: 'default'
}[status] || 'default');

export default function AssignedComplaints() {
  const queryClient = useQueryClient();
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [updateError, setUpdateError] = useState('');
  const [page, setPage] = useState(1);
  const [endDate, setEndDate] = useState(() => toDateInput(new Date()));
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return toDateInput(date);
  });

  const { data: complaintPage = { items: [], totalPages: 0, total: 0 }, isLoading, error } = useQuery(
    ['complaints', 'assigned-to-me', startDate, endDate, page],
    async () => {
      const response = await complaintsAPI.getAssignedToMe({ startDate, endDate, page });
      return response.data || { items: [], totalPages: 0, total: 0 };
    },
    { keepPreviousData: true, refetchInterval: 15000, refetchOnWindowFocus: true }
  );
  const complaints = Array.isArray(complaintPage.items) ? complaintPage.items : [];

  const statusMutation = useMutation(
    ({ complaintId, status }) => complaintsAPI.update(complaintId, { status }),
    {
      onSuccess: response => {
        const updated = response.data?.complaint;
        setSelectedComplaint(current => current && updated ? { ...current, ...updated } : current);
        setUpdateError('');
        queryClient.invalidateQueries(['complaints', 'assigned-to-me']);
      },
      onError: mutationError => {
        setUpdateError(mutationError.response?.data?.message || 'Failed to update complaint status.');
      }
    }
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, width: '100%', maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.6rem', sm: '2.125rem' }, fontWeight: 700 }}>
            Assigned Complaints
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View complaints assigned to you and update their progress.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <TextField
          label="Start date"
          type="date"
          value={startDate}
          onChange={event => {
            const value = event.target.value;
            setStartDate(value);
            const maximumEnd = new Date(`${value}T00:00:00`);
            maximumEnd.setMonth(maximumEnd.getMonth() + 6);
            if (new Date(`${endDate}T00:00:00`) > maximumEnd) setEndDate(toDateInput(maximumEnd));
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End date"
          type="date"
          value={endDate}
          onChange={event => {
            const value = event.target.value;
            setEndDate(value);
            const minimumStart = new Date(`${value}T00:00:00`);
            minimumStart.setMonth(minimumStart.getMonth() - 6);
            if (new Date(`${startDate}T00:00:00`) < minimumStart) setStartDate(toDateInput(minimumStart));
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {PAGE_SIZE} complaints per page · maximum range 6 months
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.response?.data?.message || 'Failed to load assigned complaints.'}</Alert>}
      {isLoading ? (
        <Typography>Loading assigned complaints...</Typography>
      ) : complaints.length === 0 ? (
        <Alert severity="info">No complaints are currently assigned to you.</Alert>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
          {complaints.map(complaint => (
            <Card key={complaint.complaintId} variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
                    <Assignment color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{complaint.title}</Typography>
                  </Box>
                  <Chip label={String(complaint.status || 'OPEN').replaceAll('_', ' ')} color={statusColor(complaint.status)} size="small" />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{complaint.description}</Typography>
                <Typography variant="body2"><strong>Flat:</strong> {complaint.flatNumber || complaint.flatId}</Typography>
                <Typography variant="body2"><strong>Category:</strong> {complaint.category || '-'}</Typography>
                <Typography variant="body2"><strong>Priority:</strong> {complaint.priority || 'MEDIUM'}</Typography>
                <Button sx={{ mt: 2 }} variant="contained" onClick={() => { setSelectedComplaint(complaint); setUpdateError(''); }}>
                  View and update
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {complaintPage.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={complaintPage.totalPages}
            page={page}
            onChange={(_event, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      <Dialog open={Boolean(selectedComplaint)} onClose={() => setSelectedComplaint(null)} fullWidth maxWidth="sm">
        {selectedComplaint && (
          <>
            <DialogTitle>{selectedComplaint.title}</DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
              {updateError && <Alert severity="error">{updateError}</Alert>}
              <Typography>{selectedComplaint.description}</Typography>
              <Typography variant="body2"><strong>Flat:</strong> {selectedComplaint.flatNumber || selectedComplaint.flatId}</Typography>
              <Typography variant="body2"><strong>Category:</strong> {selectedComplaint.category || '-'}</Typography>
              <Typography variant="body2"><strong>Priority:</strong> {selectedComplaint.priority || 'MEDIUM'}</Typography>
              <TextField
                select
                label="Status"
                value={selectedComplaint.status || 'OPEN'}
                onChange={event => statusMutation.mutate({ complaintId: selectedComplaint.complaintId, status: event.target.value })}
                disabled={statusMutation.isLoading}
                fullWidth
              >
                {STATUSES.map(status => <MenuItem key={status} value={status}>{status.replaceAll('_', ' ')}</MenuItem>)}
              </TextField>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedComplaint(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
